/**
 * Hook para verificar permisos del usuario actual.
 *
 * Combina el motor PERMS (defaults en `@constants/roles`) con los overrides
 * editables del ADMIN_PMIS desde el módulo "Autoridades → Matriz de permisos":
 *   1. Si la matriz tiene una entrada para este permiso → su valor manda.
 *   2. Si no → cae al default de PERMS.
 *
 * Reactivo: se suscribe a cambios de la matriz (evento custom + storage event
 * para multi-pestañas) y re-evalúa en cada render.
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "./useAuth";
import { can as canDefault, type Role } from "@constants/roles";
import {
  leerMatriz,
  construirPermsDesdeMatriz,
  MATRIZ_PERMS_EVENT,
} from "@features/autoridades/matrizPermisos";

export function usePermissions() {
  const { user } = useAuth();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setVersion((v) => v + 1);
    window.addEventListener(MATRIZ_PERMS_EVENT, handler);
    // Multi-pestañas: el storage event solo dispara en otras pestañas.
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(MATRIZ_PERMS_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  // Recalcular overrides cuando cambia la matriz. Memoizado para que no se
  // reconstruya en cada render que no sea relevante.
  const overrides = useMemo(
    () => construirPermsDesdeMatriz(leerMatriz()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version],
  );

  const can = useCallback(
    (perm: string): boolean => {
      const role: Role | null = user?.role ?? null;
      if (!role) return false;
      // ADMIN_PMIS sigue con bypass total (auditado por log inmutable).
      if (role === "ADMIN_PMIS") return true;
      // 1) Override de la matriz si la clave está mapeada.
      if (Object.prototype.hasOwnProperty.call(overrides, perm)) {
        return Boolean(overrides[perm]?.[role]);
      }
      // 2) Default del motor PERMS.
      return canDefault(role, perm);
    },
    [user?.role, overrides],
  );

  return {
    can,
    role: user?.role ?? null,
  };
}
